import type { VodChapter } from 'src/generated/graphql-manager';

import { convertToSeconds, offsetInMsToString } from '../../event-summary-cue-points/format-time';

/**
 * Returns the next offset time as a formatted string ('hh:mm:ss')
 * If no value is provided, returns '00:00:00'
 * Otherwise adds 1 second to the previous offset
 */
export const getNextOffset = (value?: string): string => {
  if (!value) return '00:00:00';

  const previousOffset = convertToSeconds(value, 'hh:mm:ss').value;
  const nextOffset = previousOffset + 1;

  return offsetInMsToString(nextOffset * 1000, true);
};

/**
 * Checks if a string is in the format 'hh:mm:ss'
 */
export const isValidTimeFormat = (value: string): boolean => {
  return /^\d{2}:\d{2}:\d{2}$/.test(value);
};

/**
 * Checks if chapter offsets are in ascending order
 */
export const areChaptersInOrder = (chapters: VodChapter[]): boolean => {
  for (let i = 1; i < chapters.length; i++) {
    const prevOffset = convertToSeconds(chapters[i - 1].offsetInMs, 'hh:mm:ss').value;
    const currOffset = convertToSeconds(chapters[i].offsetInMs, 'hh:mm:ss').value;

    if (currOffset <= prevOffset) return false;
  }

  return true;
};

/**
 * Prepares chapters data from backend format to UI format
 */
export const prepareChapters = (chapters: VodChapter[], languages: { code: string; name?: string }[]): VodChapter[] =>
  chapters.map(({ __typename, ...chapter }) => ({
    ...chapter,
    offsetInMs: offsetInMsToString(chapter.offsetInMs || 0, true),
    titles: languages.map((lang) => {
      const titleForLang = chapter.titles?.find((t) => t.language === lang.code);
      return { language: lang.code, text: titleForLang?.text ?? '' };
    }),
  }));
